import type { Bounds } from './types';

/**
 * Arithmétique des widgets numériques (curseur, compteur, étoiles) : des mesures, jamais des montants
 * (R2 : un montant passe par le décimal du Core et n'a pas de widget ici). Même calcul que la tablette
 * (`slider_field.dart`, `field_frame.dart`).
 */

/** Crans d'un curseur au plus. */
export const MAX_DIVISIONS = 1000;

/** Nombre de décimales significatives d'un nombre fini (« 4.25 » : 2) ; une écriture exponentielle : 0. */
export function decimalsOf(value: number): number {
  const textual = String(value);
  if (!textual.includes('.') || textual.includes('e')) return 0;
  return textual.split('.')[1]!.replace(/0+$/, '').length;
}

export function clamp(value: number, min: number | undefined, max: number | undefined): number {
  let out = value;
  if (min !== undefined && out < min) out = min;
  if (max !== undefined && out > max) out = max;
  return out;
}

/**
 * Bornes de saisie d'un curseur : `validation.min/max` quand le champ déclare les deux (bornes dures :
 * une mesure hors cible reste saisissable), sinon celles de la cible ; `null` sans bornes finies.
 */
export function sliderRange(validation: Bounds | undefined, target: Bounds | undefined): { min: number; max: number } | null {
  for (const bounds of [validation, target]) {
    const min = bounds?.min;
    const max = bounds?.max;
    if (min !== undefined && max !== undefined && Number.isFinite(min) && Number.isFinite(max) && min < max) return { min, max };
  }
  return null;
}

/**
 * Pas d'un curseur. Pas déclaré (`presentation.step`, inspection-template-body 1.9.0) : il prime, compté en
 * unités entières de la précision du pas et de la borne basse (au plus six décimales), s'il donne entre
 * un et mille crans (un pas décimal sur un entier est ignoré). Sinon le pas déduit : 1 pour un entier ;
 * sinon le dixième, ou la précision des bornes si elle est plus fine (au plus trois décimales), sans
 * dépasser mille crans. `unit` : unités de 10^-decimals par cran.
 */
export function sliderSteps(type: string, min: number, max: number, declared?: number): { decimals: number; divisions: number; unit: number } {
  if (declared !== undefined && Number.isFinite(declared) && declared > 0 && (type !== 'integer' || Number.isInteger(declared))) {
    const decimals = Math.min(6, Math.max(decimalsOf(declared), decimalsOf(min)));
    const scale = 10 ** decimals;
    const unit = Math.round(declared * scale);
    const divisions = Math.floor(Math.round((max - min) * scale) / unit);
    if (unit >= 1 && divisions >= 1 && divisions <= MAX_DIVISIONS) return { decimals, divisions, unit };
  }
  let decimals = type === 'integer' ? 0 : Math.min(3, Math.max(1, decimalsOf(min), decimalsOf(max)));
  const divisions = (): number => Math.round((max - min) * 10 ** decimals);
  while (decimals > 0 && divisions() > MAX_DIVISIONS) decimals--;
  return { decimals, divisions: Math.max(1, divisions()), unit: 1 };
}

/** Valeur d'un cran, calculée en unités entières du pas (jamais une somme de flottants qui dérive). */
export function sliderValueAt(min: number, decimals: number, step: number, unit = 1): number {
  if (decimals === 0) return Math.round(min) + step * unit;
  const scale = 10 ** decimals;
  return (Math.round(min * scale) + step * unit) / scale;
}

/** Cran le plus proche d'une valeur. */
export function sliderStepOf(min: number, decimals: number, value: number, unit = 1): number {
  return Math.round(((value - min) * 10 ** decimals) / unit);
}

/**
 * Valeur suivante d'un compteur : la valeur (0 au premier appui) plus ou moins le pas (déclaré, sinon 1),
 * à la précision la plus fine des deux, sans dérive de virgule flottante, ramenée dans les bornes dures.
 */
export function stepperNext(current: number | null, direction: 1 | -1, step: number | undefined, min: number | undefined, max: number | undefined): number {
  const pace = step !== undefined && Number.isFinite(step) && step > 0 ? step : 1;
  const base = current ?? 0;
  const scale = 10 ** Math.min(10, Math.max(decimalsOf(base), decimalsOf(pace)));
  return clamp((Math.round(base * scale) + direction * Math.round(pace * scale)) / scale, min, max);
}

/** Note sur 10 quand la borne haute (validation, sinon cible) vaut 10, sinon sur 5 (comme la tablette). */
export function starCount(validation: Bounds | undefined, target: Bounds | undefined): 5 | 10 {
  return sliderRange(validation, target)?.max === 10 || target?.max === 10 ? 10 : 5;
}
